import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ExternalLink, Eye, EyeOff, Loader2, Mail, RefreshCw, Send, X } from 'lucide-react';
import {
  fetchAdminEmailTemplate,
  fetchAdminEmailTemplates,
  previewEmailPortalEmail,
  sendEmailPortalEmail,
  updateFoundingPartnerTouchStatus,
  type AdminEmailPreview,
  type AdminEmailTemplate,
  type AdminEmailTemplateSummary,
  type AppIdentity,
  type EmailPortalSendResponse,
  type FoundingPartnerOutreachProspect,
} from '../../../lib/adminTools';
import { Button } from '../../ui/button';

export type RecipientPartner = {
  tenantSlug: string;
  partnerName: string;
  contactName: string;
  recipientEmail: string;
  outreachUrl?: string | null;
  touchIdentifier?: number | string | null;
};

type FunnelEmailComposerModalProps = {
  isOpen: boolean;
  onClose: () => void;
  recipients: RecipientPartner[];
  identity: AppIdentity | null;
  theme: 'light' | 'dark';
  onSendComplete?: (results: Array<{ recipient: RecipientPartner; success: boolean; response?: EmailPortalSendResponse; error?: string }>) => void;
};

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

export default function FunnelEmailComposerModal({
  isOpen,
  onClose,
  recipients,
  identity,
  theme,
  onSendComplete,
}: FunnelEmailComposerModalProps) {
  const isDark = theme === 'dark';

  // Templates state
  const [templates, setTemplates] = useState<AdminEmailTemplateSummary[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [loadedTemplate, setLoadedTemplate] = useState<AdminEmailTemplate | null>(null);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [templateLoadError, setTemplateLoadError] = useState('');

  // Selected recipient for preview / editing active index
  const [activeRecipientIndex, setActiveRecipientIndex] = useState(0);

  // Form fields
  const [subject, setSubject] = useState('');
  const [personalMessage, setPersonalMessage] = useState('');
  const [isSubjectUserEdited, setIsSubjectUserEdited] = useState(false);
  const [isMessageUserEdited, setIsMessageUserEdited] = useState(false);

  // Preview state
  const [preview, setPreview] = useState<AdminEmailPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewWidth, setPreviewWidth] = useState<'desktop' | 'mobile'>('desktop');
  const [previewError, setPreviewError] = useState('');

  // Sending state
  const [sending, setSending] = useState(false);
  const [sendProgress, setSendProgress] = useState<{ sent: number; total: number } | null>(null);
  const [sendConfirmation, setSendConfirmation] = useState<boolean>(false);
  const [error, setError] = useState('');
  const [batchResults, setBatchResults] = useState<Array<{
    recipient: RecipientPartner;
    success: boolean;
    response?: EmailPortalSendResponse;
    error?: string;
  }> | null>(null);

  const activeRecipient: RecipientPartner | undefined = recipients[activeRecipientIndex] || recipients[0];

  // Theme styling helpers
  const tableText = isDark ? 'text-slate-100' : 'text-zinc-900';
  const mutedText = isDark ? 'text-slate-400' : 'text-zinc-500';
  const inputStyle = `w-full rounded-lg border px-3 py-2 text-xs transition-colors ${
    isDark
      ? 'border-slate-700 bg-slate-900/90 text-slate-100 placeholder:text-slate-500 focus:border-blue-500 focus:outline-none'
      : 'border-zinc-200 bg-white text-zinc-900 placeholder:text-zinc-400 focus:border-blue-500 focus:outline-none'
  }`;

  // Load templates on mount / open
  useEffect(() => {
    if (!isOpen) return;
    let active = true;
    setTemplatesLoading(true);
    setTemplateLoadError('');
    setBatchResults(null);
    setSendConfirmation(false);
    setError('');

    fetchAdminEmailTemplates(identity)
      .then((res) => {
        if (!active) return;
        setTemplates(res.templates);
        if (res.templates.length > 0) {
          // If a partner has warrior-trading, suggest Warrior template if present, else first
          const isWarrior = recipients.some((r) => r.tenantSlug === 'warrior-trading');
          const warriorTemplate = res.templates.find((t) => t.systemKey === 'warrior_trading_partnership' || /warrior/i.test(t.name));
          const defaultTpl = (isWarrior && warriorTemplate) ? warriorTemplate : res.templates[0];
          setSelectedTemplateId(defaultTpl.id);
        }
      })
      .catch((err) => {
        if (!active) return;
        setTemplateLoadError(err instanceof Error ? err.message : 'Unable to load email templates.');
      })
      .finally(() => {
        if (active) setTemplatesLoading(false);
      });

    return () => {
      active = false;
    };
  }, [isOpen, identity, recipients]);

  // Load template details when selected template changes
  useEffect(() => {
    if (!selectedTemplateId || !isOpen) return;
    let active = true;
    setPreview(null);
    setPreviewError('');

    fetchAdminEmailTemplate(identity, selectedTemplateId)
      .then((res) => {
        if (!active) return;
        setLoadedTemplate(res.template);
        if (!isSubjectUserEdited) {
          setSubject(res.template.subject || '');
        }
        if (!isMessageUserEdited) {
          setPersonalMessage(res.template.defaultPersonalMessage || '');
        }
      })
      .catch((err) => {
        if (!active) return;
        setPreviewError(err instanceof Error ? err.message : 'Unable to fetch template details.');
      });

    return () => {
      active = false;
    };
  }, [selectedTemplateId, identity, isOpen, isSubjectUserEdited, isMessageUserEdited]);

  // Preview action
  const handlePreview = useCallback(async () => {
    if (!selectedTemplateId || !activeRecipient) return;
    setPreviewLoading(true);
    setPreviewError('');
    try {
      const res = await previewEmailPortalEmail(identity, {
        templateId: selectedTemplateId,
        recipientEmail: activeRecipient.recipientEmail,
        recipientName: activeRecipient.contactName || activeRecipient.partnerName,
        subject: subject.trim() || undefined,
        personalMessage: personalMessage,
      });
      setPreview(res.preview);
    } catch (err) {
      setPreviewError(err instanceof Error ? err.message : 'Unable to preview email.');
    } finally {
      setPreviewLoading(false);
    }
  }, [selectedTemplateId, activeRecipient, identity, subject, personalMessage]);

  // Send single/batch emails
  const handleExecuteSend = async () => {
    if (!selectedTemplateId) {
      setError('Please select an email template.');
      return;
    }
    if (!subject.trim()) {
      setError('Subject line is required.');
      return;
    }
    const validRecipients = recipients.filter((r) => r.recipientEmail && isValidEmail(r.recipientEmail));
    if (validRecipients.length === 0) {
      setError('No recipients with valid email addresses found.');
      return;
    }

    setSending(true);
    setError('');
    const results: Array<{
      recipient: RecipientPartner;
      success: boolean;
      response?: EmailPortalSendResponse;
      error?: string;
    }> = [];

    const total = validRecipients.length;
    setSendProgress({ sent: 0, total });

    for (let i = 0; i < validRecipients.length; i++) {
      const recipient = validRecipients[i];
      const idempotencyKey = `funnel-send-${recipient.tenantSlug}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      try {
        const sendRes = await sendEmailPortalEmail(identity, {
          templateId: selectedTemplateId,
          recipientEmail: recipient.recipientEmail,
          recipientName: recipient.contactName || recipient.partnerName,
          subject: subject.trim(),
          personalMessage: personalMessage,
          idempotencyKey,
        });

        // Record touch tracking in founding partner tracking database
        try {
          const sentAt = sendRes.sentAt || new Date().toISOString();
          const targetTouchId = recipient.touchIdentifier || 0;
          await updateFoundingPartnerTouchStatus(identity, recipient.tenantSlug, targetTouchId, {
            emailSentAt: sentAt,
            notes: `Sent email via template "${sendRes.templateName}" (Subject: ${sendRes.subject})`,
          });
        } catch (touchErr) {
          console.warn('[funnel-email] touch status update failed:', touchErr);
        }

        results.push({
          recipient,
          success: true,
          response: sendRes,
        });
      } catch (sendErr) {
        results.push({
          recipient,
          success: false,
          error: sendErr instanceof Error ? sendErr.message : 'Send failed',
        });
      }

      setSendProgress({ sent: i + 1, total });
    }

    setSending(false);
    setSendProgress(null);
    setSendConfirmation(false);
    setBatchResults(results);

    if (onSendComplete) {
      onSendComplete(results);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/75 p-4"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !sending) {
          onClose();
        }
      }}
    >
      <div
        className={`flex max-h-[min(92vh,850px)] w-full max-w-5xl flex-col rounded-xl border shadow-2xl transition-all ${
          isDark ? 'border-slate-700 bg-[#11151c]' : 'border-zinc-200 bg-white'
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="funnel-email-composer-title"
      >
        {/* HEADER */}
        <div className={`flex items-start justify-between border-b px-5 py-4 ${isDark ? 'border-slate-800' : 'border-zinc-200'}`}>
          <div>
            <h2 id="funnel-email-composer-title" className={`text-base font-semibold ${tableText}`}>
              Send Email to Founding Partners
            </h2>
            <p className={`mt-0.5 text-xs ${mutedText}`}>
              Compose, preview, and send personalized emails using existing Email Template Manager templates.
            </p>
          </div>
          <button
            type="button"
            className={`rounded p-1 text-slate-400 hover:text-slate-200 disabled:opacity-50`}
            onClick={onClose}
            disabled={sending}
            aria-label="Close modal"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* BODY */}
        <div className="min-h-0 flex-1 overflow-y-auto p-5">
          {batchResults ? (
            /* RESULTS VIEW */
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className={`text-sm font-semibold ${tableText}`}>Email Delivery Results</h3>
                <span className="text-xs">
                  {batchResults.filter((r) => r.success).length} of {batchResults.length} sent successfully
                </span>
              </div>
              <div className="space-y-2">
                {batchResults.map((res) => (
                  <div
                    key={res.recipient.tenantSlug}
                    className={`flex items-center justify-between rounded-lg border p-3 text-xs ${
                      isDark ? 'border-slate-800 bg-slate-900/60' : 'border-zinc-200 bg-zinc-50'
                    }`}
                  >
                    <div>
                      <span className={`font-semibold ${tableText}`}>{res.recipient.partnerName}</span>
                      <span className={`ml-2 ${mutedText}`}>({res.recipient.recipientEmail})</span>
                    </div>
                    <div>
                      {res.success ? (
                        <span className="rounded bg-emerald-500/15 px-2 py-0.5 font-semibold text-emerald-400">
                          Sent
                        </span>
                      ) : (
                        <span className="rounded bg-rose-500/15 px-2 py-0.5 font-semibold text-rose-400">
                          Failed: {res.error || 'Unknown error'}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            /* COMPOSER FORM */
            <div className="grid gap-6 lg:grid-cols-[1.1fr_1.3fr]">
              {/* LEFT COLUMN: Configuration */}
              <div className="space-y-4">
                {/* SECTION 1: RECIPIENTS */}
                <div>
                  <div className="flex items-center justify-between">
                    <span className={`text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-teal-400' : 'text-teal-600'}`}>
                      SECTION 1 — RECIPIENTS ({recipients.length})
                    </span>
                    {recipients.length > 1 ? (
                      <span className={`text-[11px] ${mutedText}`}>
                        Previewing #{activeRecipientIndex + 1}: {activeRecipient?.partnerName}
                      </span>
                    ) : null}
                  </div>
                  <div className={`mt-2 max-h-36 overflow-y-auto rounded-lg border p-2 space-y-1 ${isDark ? 'border-slate-800 bg-slate-950/60' : 'border-zinc-200 bg-zinc-50'}`}>
                    {recipients.map((r, idx) => {
                      const isSelected = idx === activeRecipientIndex;
                      const hasValid = isValidEmail(r.recipientEmail);
                      return (
                        <div
                          key={r.tenantSlug}
                          onClick={() => {
                            setActiveRecipientIndex(idx);
                            setPreview(null);
                          }}
                          className={`flex cursor-pointer items-center justify-between rounded px-2.5 py-1.5 text-xs transition-colors ${
                            isSelected
                              ? isDark ? 'bg-blue-600/25 border border-blue-500/40 text-blue-200' : 'bg-blue-50 border border-blue-300 text-blue-900'
                              : isDark ? 'hover:bg-slate-800 text-slate-300' : 'hover:bg-zinc-200/60 text-zinc-700'
                          }`}
                        >
                          <div className="truncate">
                            <span className="font-semibold">{r.partnerName}</span>
                            <span className={`ml-2 ${mutedText}`}>&lt;{r.recipientEmail || 'no email'}&gt;</span>
                          </div>
                          {!hasValid ? (
                            <span className="shrink-0 rounded bg-rose-500/15 px-1.5 py-0.5 text-[10px] text-rose-400">
                              Invalid Email
                            </span>
                          ) : isSelected ? (
                            <span className="shrink-0 text-[10px] font-semibold text-blue-400">
                              Previewing
                            </span>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* SECTION 2: EMAIL TEMPLATE */}
                <div>
                  <label className={`block text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-teal-400' : 'text-teal-600'}`}>
                    SECTION 2 — EMAIL TEMPLATE
                  </label>
                  <div className="relative mt-1.5">
                    <select
                      value={selectedTemplateId}
                      onChange={(e) => {
                        setSelectedTemplateId(e.target.value);
                        setIsSubjectUserEdited(false);
                        setIsMessageUserEdited(false);
                        setPreview(null);
                      }}
                      disabled={templatesLoading || !templates.length}
                      className={`${inputStyle} appearance-none pr-8 admin-tools-custom-chevron`}
                    >
                      {templatesLoading ? <option value="">Loading templates...</option> : null}
                      {templates.map((tpl) => (
                        <option key={tpl.id} value={tpl.id}>
                          {tpl.name}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  </div>
                  {templateLoadError ? (
                    <p className="mt-1 text-xs text-rose-400">{templateLoadError}</p>
                  ) : null}
                </div>

                {/* SECTION 3: SUBJECT */}
                <div>
                  <div className="flex items-center justify-between">
                    <label className={`block text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-teal-400' : 'text-teal-600'}`}>
                      SECTION 3 — SUBJECT
                    </label>
                    {isSubjectUserEdited ? (
                      <button
                        type="button"
                        onClick={() => {
                          setIsSubjectUserEdited(false);
                          setSubject(loadedTemplate?.subject || '');
                          setPreview(null);
                        }}
                        className="text-[11px] text-blue-400 hover:underline"
                      >
                        Reset to template default
                      </button>
                    ) : null}
                  </div>
                  <input
                    type="text"
                    value={subject}
                    onChange={(e) => {
                      setSubject(e.target.value);
                      setIsSubjectUserEdited(true);
                      setPreview(null);
                    }}
                    placeholder="Enter email subject line..."
                    className={`mt-1.5 ${inputStyle}`}
                  />
                </div>

                {/* SECTION 4: PERSONAL MESSAGE */}
                <div>
                  <div className="flex items-center justify-between">
                    <label className={`block text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-teal-400' : 'text-teal-600'}`}>
                      SECTION 4 — PERSONAL MESSAGE
                    </label>
                    {isMessageUserEdited ? (
                      <button
                        type="button"
                        onClick={() => {
                          setIsMessageUserEdited(false);
                          setPersonalMessage(loadedTemplate?.defaultPersonalMessage || '');
                          setPreview(null);
                        }}
                        className="text-[11px] text-blue-400 hover:underline"
                      >
                        Reset to default
                      </button>
                    ) : null}
                  </div>
                  <textarea
                    rows={4}
                    value={personalMessage}
                    onChange={(e) => {
                      setPersonalMessage(e.target.value);
                      setIsMessageUserEdited(true);
                      setPreview(null);
                    }}
                    placeholder="Custom message to be personalized for each recipient..."
                    className={`mt-1.5 ${inputStyle} min-h-24 resize-y leading-relaxed`}
                  />
                  <p className={`mt-1 text-[11px] ${mutedText}`}>
                    Supports markdown bold (<code>**text**</code>) and multiple paragraphs.
                  </p>
                </div>
              </div>

              {/* RIGHT COLUMN: Preview & Confirmation */}
              <div className="flex flex-col space-y-4">
                {/* SECTION 5: PREVIEW */}
                <div className="flex flex-1 flex-col">
                  <div className="flex items-center justify-between">
                    <span className={`text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-teal-400' : 'text-teal-600'}`}>
                      SECTION 5 — PREVIEW
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setPreviewWidth('desktop')}
                        className={`rounded px-2 py-0.5 text-xs font-medium ${
                          previewWidth === 'desktop'
                            ? isDark ? 'bg-slate-700 text-white' : 'bg-blue-600 text-white'
                            : mutedText
                        }`}
                      >
                        Desktop
                      </button>
                      <button
                        type="button"
                        onClick={() => setPreviewWidth('mobile')}
                        className={`rounded px-2 py-0.5 text-xs font-medium ${
                          previewWidth === 'mobile'
                            ? isDark ? 'bg-slate-700 text-white' : 'bg-blue-600 text-white'
                            : mutedText
                        }`}
                      >
                        Mobile
                      </button>
                      <Button
                        type="button"
                        variant="secondary"
                        className="h-7 border border-slate-700 px-2.5 text-xs"
                        onClick={handlePreview}
                        disabled={previewLoading || !selectedTemplateId}
                      >
                        {previewLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Eye className="h-3 w-3 mr-1" />}
                        Preview Email
                      </Button>
                    </div>
                  </div>

                  <div className={`mt-2 flex-1 min-h-[340px] rounded-lg border overflow-hidden flex flex-col ${isDark ? 'border-slate-800 bg-slate-950/80' : 'border-zinc-200 bg-zinc-100'}`}>
                    {preview ? (
                      <div className="flex flex-1 flex-col overflow-hidden">
                        <div className={`border-b px-3 py-1.5 text-[11px] ${isDark ? 'border-slate-800 bg-slate-900 text-slate-300' : 'border-zinc-200 bg-white text-zinc-700'}`}>
                          <div><b>To:</b> {activeRecipient?.contactName || activeRecipient?.partnerName} &lt;{activeRecipient?.recipientEmail}&gt;</div>
                          <div><b>Subject:</b> {preview.subject}</div>
                        </div>
                        <div className="flex-1 overflow-auto p-2 bg-slate-900/30">
                          <iframe
                            title="Funnel email preview"
                            sandbox=""
                            referrerPolicy="no-referrer"
                            srcDoc={preview.html}
                            className="mx-auto block rounded bg-white shadow-sm"
                            style={{
                              width: previewWidth === 'mobile' ? 360 : '100%',
                              height: 380,
                              border: 0,
                            }}
                          />
                        </div>
                        {/* Plain text fallback */}
                        <details className={`border-t px-3 py-1.5 text-xs ${isDark ? 'border-slate-800 bg-slate-900' : 'border-zinc-200 bg-white'}`}>
                          <summary className="cursor-pointer font-semibold text-[11px] text-blue-400">
                            Plain-text fallback preview
                          </summary>
                          <pre className={`mt-1.5 max-h-32 overflow-y-auto whitespace-pre-wrap font-sans text-[11px] ${mutedText}`}>
                            {preview.text}
                          </pre>
                        </details>
                      </div>
                    ) : (
                      <div className="flex flex-1 flex-col items-center justify-center p-6 text-center">
                        <Mail className={`h-8 w-8 ${mutedText} mb-2 opacity-50`} />
                        <p className={`text-xs ${mutedText}`}>
                          Click <b>Preview Email</b> to generate an HTML preview personalized for{' '}
                          <span className={tableText}>{activeRecipient?.contactName || activeRecipient?.partnerName}</span>.
                        </p>
                      </div>
                    )}
                  </div>
                  {previewError ? (
                    <p className="mt-1 text-xs text-rose-400">{previewError}</p>
                  ) : null}
                </div>

                {/* CONFIRMATION PROMPT */}
                {sendConfirmation ? (
                  <div className={`rounded-lg border p-4 ${isDark ? 'border-amber-500/40 bg-amber-500/10 text-amber-200' : 'border-amber-300 bg-amber-50 text-amber-900'}`}>
                    <h4 className="text-xs font-semibold">Confirm Delivery</h4>
                    <p className="mt-1 text-xs">
                      You are about to send <b>{recipients.length}</b> personalized email{recipients.length === 1 ? '' : 's'} using template "<b>{loadedTemplate?.name || 'Selected Template'}</b>".
                    </p>
                    <div className="mt-3 flex gap-2">
                      <Button
                        type="button"
                        className="h-8 bg-emerald-600 hover:bg-emerald-500 text-white px-3 text-xs"
                        onClick={handleExecuteSend}
                        disabled={sending}
                      >
                        {sending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Send className="h-3 w-3 mr-1" />}
                        Confirm &amp; Send Now
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        className="h-8 px-3 text-xs"
                        onClick={() => setSendConfirmation(false)}
                        disabled={sending}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          )}

          {error ? (
            <p className="mt-4 rounded-lg border border-rose-500/40 bg-rose-500/10 p-3 text-xs text-rose-400">
              {error}
            </p>
          ) : null}
        </div>

        {/* FOOTER */}
        <div className={`flex items-center justify-between border-t px-5 py-3.5 ${isDark ? 'border-slate-800' : 'border-zinc-200'}`}>
          {sendProgress ? (
            <div className="flex items-center gap-2 text-xs text-blue-400">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              <span>Sending email {sendProgress.sent} of {sendProgress.total}...</span>
            </div>
          ) : (
            <div className={`text-xs ${mutedText}`}>
              {batchResults ? `${batchResults.length} emails processed.` : `${recipients.length} partner recipient${recipients.length === 1 ? '' : 's'} selected.`}
            </div>
          )}

          <div className="flex items-center gap-2">
            {batchResults ? (
              <Button type="button" variant="secondary" className="h-8 px-4 text-xs" onClick={onClose}>
                Close
              </Button>
            ) : (
              <>
                <Button type="button" variant="secondary" className="h-8 px-3 text-xs" onClick={onClose} disabled={sending}>
                  Cancel
                </Button>
                {!sendConfirmation ? (
                  <Button
                    type="button"
                    className="h-8 bg-[#61E307] hover:bg-[#52c406] text-[#020617] font-semibold px-4 text-xs"
                    onClick={() => {
                      if (!selectedTemplateId) {
                        setError('Please select an email template.');
                        return;
                      }
                      if (!subject.trim()) {
                        setError('Subject line is required.');
                        return;
                      }
                      setSendConfirmation(true);
                    }}
                    disabled={sending || templatesLoading || recipients.length === 0}
                  >
                    Send Email ({recipients.length})
                  </Button>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
